import { ContainerSize, Settings, WhistleblowerForm } from '@lib/types';
import Container from '@components/container';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Input from '@components/input';
import Select from '@components/select';
import SubmitButton from '@components/submit-button';
import Textarea from '@components/textarea';
import { useRef, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import RadioGroup from '@components/radio-group';

type WhistleblowerProps = {
  data: WhistleblowerForm;
  settings: Partial<Settings>;
};

const formSchema = z.object({
  currentEmployee: z.literal('yes').or(z.literal('no')).optional(),
  relationshipToDistalmotion: z.string().optional(),
  involvedIndividuals: z.string().min(1, 'Required'),
  incidentTime: z.string(),
  moreThanOnce: z.literal('yes').or(z.literal('no')).optional(),
  typeOfIncident: z.string().min(1, 'Required'),
  incidentDescription: z.string().min(1, 'Required'),
  managementInvolved: z.literal('yes').or(z.literal('no')).optional(),
  reportedInternally: z.literal('yes').or(z.literal('no')).optional(),
  reportedExternally: z.literal('yes').or(z.literal('no')).optional(),
  witnesses: z.literal('yes').or(z.literal('no')).optional(),
  namesOfWitnesses: z.string().optional(),
  filesAvailable: z.literal('yes').or(z.literal('no')).optional(),
  files: z
    .custom<FileList>(
      (value) => value instanceof FileList && value.length > 0,
      {
        message: 'Please upload at least one file',
      },
    )
    .optional()
    .refine(
      (files) =>
        Array.from(files ?? []).every((file) => file.size <= 5 * 1024 * 1024),
      { message: 'Each file must be less than 5MB' },
    ),
  willingToAssist: z.literal('yes').or(z.literal('no')).optional(),
  email: z.union([z.literal(''), z.string().email('Invalid email')]),
});

type FormSchemaType = z.infer<typeof formSchema>;

export default function WhistleblowerFormSection({
  data: { title, paragraph, notice, to },
  settings,
}: WhistleblowerProps) {
  const successMsg = 'Report successfully sent!';
  const errorMsg = 'Report could not be sent. Please try again later.';

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [submitState, setSubmitState] =
    useState<'initial' | 'success' | 'error'>('initial');

  const {
    control,
    formState: { dirtyFields, errors, isSubmitting },
    handleSubmit,
    register,
    reset,
  } = useForm<FormSchemaType>({
    defaultValues: {
      currentEmployee: undefined,
      relationshipToDistalmotion: '',
      involvedIndividuals: '',
      incidentTime: '',
      moreThanOnce: undefined,
      typeOfIncident: '',
      incidentDescription: '',
      managementInvolved: undefined,
      reportedInternally: undefined,
      reportedExternally: undefined,
      witnesses: undefined,
      namesOfWitnesses: '',
      filesAvailable: undefined,
      files: undefined,
      willingToAssist: undefined,
      email: '',
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onSubmit: SubmitHandler<FormSchemaType> = async (data) => {
    const formData = new FormData();

    formData.append('currentEmployee', data.currentEmployee ?? 'Not provided');
    formData.append('involvedIndividuals', data.involvedIndividuals);
    formData.append('incidentTime', data.incidentTime ?? 'Not provided');
    formData.append('moreThanOnce', data.moreThanOnce ?? 'Not provided');
    formData.append('typeOfIncident', data.typeOfIncident);
    formData.append('incidentDescription', data.incidentDescription);
    formData.append(
      'managementInvolved',
      data.managementInvolved ?? 'Not provided',
    );
    formData.append(
      'reportedInternally',
      data.reportedInternally ?? 'Not provided',
    );
    formData.append(
      'reportedExternally',
      data.reportedExternally ?? 'Not provided',
    );
    formData.append('witnesses', data.witnesses ?? 'Not provided');
    formData.append(
      'namesOfWitnesses',
      data.namesOfWitnesses ?? 'Not provided',
    );
    formData.append('filesAvailable', data.filesAvailable ?? 'Not provided');
    formData.append('willingToAssist', data.willingToAssist ?? 'Not provided');
    formData.append('email', data.email ?? 'Not provided');
    formData.append('to', to);

    for (let i = 0; i < (data.files ?? []).length; i++) {
      if (data.files) {
        formData.append('files', data.files[i]);
      }
    }

    const response = await fetch('/api/send-email', {
      method: 'POST',
      body: formData,
    });

    if (response.ok) {
      reset();
      return setSubmitState('success');
    }

    setSubmitState('error');
  };

  return (
    <Container size={ContainerSize.wide}>
      <div className="grid grid-cols-12 gap-1.5 md:gap-6 relative">
        <div className="col-start-2 col-span-10 md:col-start-4 md:col-span-6 space-y-10">
          <h1 className="text-32 md:text-35 leading-130 font-bold text-blue-800">
            {title}
          </h1>
          <div
            className="prose"
            dangerouslySetInnerHTML={{ __html: paragraph }}
          />
          <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name="currentEmployee"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Are you a current employee of Distalmotion?"
                    error={errors['currentEmployee']?.message}
                    id="currentEmployee"
                    isDirty={dirtyFields['currentEmployee']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <Textarea
              error={errors['relationshipToDistalmotion']?.message}
              label={'If No, what is your relationship to Distalmotion?'}
              id="relationshipToDistalmotion"
              isDirty={dirtyFields['relationshipToDistalmotion']}
              {...register('relationshipToDistalmotion')}
            />
            <Textarea
              error={errors['involvedIndividuals']?.message}
              label={
                'Who are the individuals involved in the incident? (Required)'
              }
              id="involvedIndividuals"
              isDirty={dirtyFields['involvedIndividuals']}
              {...register('involvedIndividuals')}
            />
            <Textarea
              error={errors['incidentTime']?.message}
              label={'When did the incident occur?'}
              id="incidentTime"
              isDirty={dirtyFields['incidentTime']}
              {...register('incidentTime')}
            />
            <Controller
              control={control}
              name="moreThanOnce"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Did the incident occur more than once?"
                    error={errors['moreThanOnce']?.message}
                    id="moreThanOnce"
                    isDirty={dirtyFields['moreThanOnce']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <Controller
              render={({ field }) => {
                return (
                  <Select
                    error={errors['typeOfIncident']?.message}
                    id="typeOfIncident"
                    label="What type of incident would you like to report? (Required). Refer to the Notice for description."
                    options={[
                      'Accounting, Auditing, and Internal financial controls',
                      'Anti-Trust',
                      'Bribery and Corruption',
                      'Compliance and Regulation Violation',
                      'Conflict of Interest',
                      'Discrimination',
                      'Fraud',
                      'Falsification of Records',
                      'Harassment',
                      'HIPPA',
                      'Hostile Work Environment',
                      'Improper giving and receiving gifts',
                      'IT Security',
                      'Data Breach',
                      'Retaliation',
                      'Safety Concerns',
                      'Sexual Harassment',
                      'Others',
                    ]}
                    selected={field.value}
                    onChange={field.onChange}
                  />
                );
              }}
              control={control}
              name="typeOfIncident"
            />
            <div>
              <Textarea
                error={errors['incidentDescription']?.message}
                label={'What would you like to report? (Required)'}
                id="incidentDescription"
                isDirty={dirtyFields['incidentDescription']}
                {...register('incidentDescription')}
                className="h-40"
              />
              <div className="text-11">
                Please provide all details regarding the alleged incident,
                including any information that could be valuable in the
                evaluation of this situation, but exercise care to not provide
                details that may reveal your identity unless you wish to do so.
              </div>
            </div>
            <Controller
              control={control}
              name="managementInvolved"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Was someone from the management involved in the incident?"
                    error={errors['managementInvolved']?.message}
                    id="managementInvolved"
                    isDirty={dirtyFields['managementInvolved']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="reportedInternally"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Has the incident been reported to anyone in Distalmotion?"
                    error={errors['reportedInternally']?.message}
                    id="reportedInternally"
                    isDirty={dirtyFields['reportedInternally']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="reportedExternally"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Has the incident been reported to anyone outside of Distalmotion?"
                    error={errors['reportedExternally']?.message}
                    id="reportedExternally"
                    isDirty={dirtyFields['reportedExternally']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="witnesses"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Were there any witnesses?"
                    error={errors['witnesses']?.message}
                    id="witnesses"
                    isDirty={dirtyFields['witnesses']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <Textarea
              error={errors['namesOfWitnesses']?.message}
              label={'If Yes, list the name(s) of the witnesses'}
              id="namesOfWitnesses"
              isDirty={dirtyFields['namesOfWitnesses']}
              {...register('namesOfWitnesses')}
            />
            <Controller
              control={control}
              name="filesAvailable"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Do you have files to support your report?"
                    error={errors['filesAvailable']?.message}
                    id="filesAvailable"
                    isDirty={dirtyFields['filesAvailable']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <Controller
              name="files"
              control={control}
              render={({ field }) => (
                <div className="relative group">
                  <label
                    htmlFor="files"
                    className="block mb-2 text-15 leading-140 text-blue-800">
                    If Yes, upload the document(s) you are willing to attach to
                    your report
                  </label>
                  <div className="flex items-center space-x-2">
                    <button
                      className="text-11 text-blue-800 font-bold border border-blue px-4 py-2 rounded-xl flex-shrink-0 uppercase"
                      type="button"
                      onClick={handleClick}>
                      Upload files
                    </button>
                    <span className="text-11">
                      {Array.from(field.value ?? [])
                        .map((file) => file.name)
                        .join(', ')}
                    </span>
                  </div>
                  <input
                    id="files"
                    type="file"
                    multiple
                    ref={fileInputRef}
                    onChange={(e) => {
                      if (e.target.files) {
                        field.onChange(e.target.files);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="willingToAssist"
              render={({ field }) => {
                return (
                  <RadioGroup
                    options={['Yes', 'No']}
                    label="Would you be willing to assist further if asked?"
                    error={errors['willingToAssist']?.message}
                    id="willingToAssist"
                    isDirty={dirtyFields['willingToAssist']}
                    onChange={field.onChange}
                    value={field.value}
                  />
                );
              }}
            />
            <div>
              <Input
                error={errors['email']?.message}
                label="If Yes, please provide your email address:"
                id="email"
                isDirty={dirtyFields['email']}
                {...register('email')}
                type="email"
              />
              <div className="text-11">
                By providing your email address, you enable Distalmotion to
                email you. Your email address will remain confidential as
                allowed by applicable law.
              </div>
            </div>
            <SubmitButton
              disabled={isSubmitting || submitState === 'success'}
              isSubmitting={isSubmitting}
              endMessage="Report sent!">
              {submitState === 'success'
                ? 'Report sent!'
                : settings.texts?.valueContactSend ?? 'Send Report'}
            </SubmitButton>
          </form>
          {submitState === 'error' && (
            <div
              className="text-error col-start-2 col-span-10 md:col-start-4 md:col-span-6 space-y-2 text-15 [&>h1]:text-22 [&>h1]:font-bold [&>h2]:text-20 [&>h2]:font-bold [&>h3]:font-bold"
              dangerouslySetInnerHTML={{ __html: errorMsg }}
            />
          )}
          {submitState === 'success' && (
            <>
              <div
                className="col-start-2 col-span-10 md:col-start-4 md:col-span-6 space-y-2 text-15 [&>h1]:text-22 [&>h1]:font-bold [&>h2]:text-20 [&>h2]:font-bold [&>h3]:font-bold"
                dangerouslySetInnerHTML={{ __html: successMsg }}
              />
            </>
          )}
        </div>
        <div
          className="col-start-2 col-span-10 md:col-start-4 md:col-span-6 space-y-2 text-15 [&>h1]:text-22 [&>h1]:font-bold [&>h2]:text-20 [&>h2]:font-bold [&>h3]:font-bold"
          dangerouslySetInnerHTML={{ __html: notice }}
        />
      </div>
    </Container>
  );
}
